import { LogicalNode, Node, ConditionNode, ExistsNode } from "app/types/record-management-types";
import { normalizeSortPreferences, SortPreferences } from "../hooks/useRecordListSort";
import {
    LogicalNode as BuilderLogicalNode,
    QueryNode as BuilderQueryNode,
    ConditionNode as BuilderConditionNode,
} from "modules/record/record-producer/types/query.types";
import SysColumn from "app/class/sys-column";

const FILTER_PARAM_KEY = "f";
const SORT_PARAM_KEY = "s";
const NULL_SORT_SENTINEL = "!";

type CompactLogicalNode = ["a" | "o", ...CompactQueryNode[]];
type CompactConditionNode = [string, string] | [string, string, unknown];
type CompactExistsNode = ["$e", string, CompactConditionNode[]];
type CompactQueryNode = CompactLogicalNode | CompactConditionNode | CompactExistsNode;

const isUnaryOperator = (operator: string) =>
    operator === "empty" || operator === "not_empty";

const RELATIVE_UNITS = new Set(["minute", "hour", "day", "week", "month", "year"]);

const columnForField = (field: string, columns: SysColumn[] = []) => {
    const rootField = field.split(".").filter(Boolean)[0];
    return columns.find((column) => column.name === rootField);
};

const isValidDateValue = (value: unknown) =>
    typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);

const isValidDateTimeValue = (value: unknown) => {
    if (typeof value !== "string" || value.trim() === "") {
        return false;
    }

    const date = new Date(value);
    return !Number.isNaN(date.getTime());
};

const sanitizeRelativeLastValue = (value: unknown, columnType?: string) => {
    if (!value || typeof value !== "object") {
        return null;
    }

    const candidate = value as { amount?: unknown; unit?: unknown };
    const amount = Number(candidate.amount);
    const unit = String(candidate.unit || "");

    if (!Number.isInteger(amount) || amount < 1 || !RELATIVE_UNITS.has(unit)) {
        return null;
    }

    if (columnType === "DATE" && !["day", "week", "month", "year"].includes(unit)) {
        return null;
    }

    return { amount, unit };
};

const sanitizeTemporalValue = (
    columnType: string | undefined,
    operator: string,
    value: unknown
) => {
    if (operator.endsWith("_dynamic")) {
        return value;
    }

    if (operator === "relative_last" || operator === "relative_next") {
        return sanitizeRelativeLastValue(value, columnType);
    }

    if (columnType === "DATE") {
        if (operator === "in" || operator === "between") {
            return Array.isArray(value) && value.every(isValidDateValue) ? value : null;
        }

        return isValidDateValue(value) ? value : null;
    }

    if (columnType === "DATETIME") {
        if (operator === "on" || operator === "not_on") {
            return isValidDateValue(value) ? value : null;
        }

        if (operator === "in") {
            return Array.isArray(value) && value.every(isValidDateTimeValue) ? value : null;
        }

        if (operator === "between") {
            return Array.isArray(value)
                ? (value.every(isValidDateTimeValue) ? value : null)
                : (isValidDateTimeValue(value) ? [value, value] : null);
        }

        return isValidDateTimeValue(value) ? value : null;
    }

    return value;
};

const encodeBase64Url = (input: string) => {
    const bytes = new TextEncoder().encode(input);
    let binary = "";

    bytes.forEach((byte) => {
        binary += String.fromCharCode(byte);
    });

    return btoa(binary)
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/g, "");
};

const decodeBase64Url = (input: string) => {
    const normalized = input
        .replace(/-/g, "+")
        .replace(/_/g, "/")
        .padEnd(Math.ceil(input.length / 4) * 4, "=");

    const binary = atob(normalized);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));

    return new TextDecoder().decode(bytes);
};

const compactQueryNode = (node: Node): CompactQueryNode => {
    if (node.type === "logical") {
        return [
            node.operator === "or" ? "o" : "a",
            ...node.conditions.map(compactQueryNode),
        ];
    }

    if (node.type === "exists") {
        return [
            "$e",
            node.table,
            node.conditions.map((condition) => compactQueryNode(condition) as CompactConditionNode),
        ];
    }

    if (isUnaryOperator(node.operator)) {
        return [node.field, node.operator];
    }

    return [node.field, node.operator, node.value];
};

const expandQueryNode = (value: CompactQueryNode): Node => {
    if (value[0] === "a" || value[0] === "o") {
        return {
            type: "logical",
            operator: value[0] === "o" ? "or" : "and",
            conditions: value.slice(1).map((child) => expandQueryNode(child as CompactQueryNode)),
        };
    }

    if (value[0] === "$e") {
        const [, table, conditions] = value as CompactExistsNode;

        return {
            type: "exists",
            table,
            conditions: conditions.map((condition) => expandQueryNode(condition) as ConditionNode),
        };
    }

    const [field, operator, conditionValue] = value as CompactConditionNode;
    const condition: ConditionNode = {
        type: "condition",
        field,
        operator,
    };

    if (!isUnaryOperator(operator)) {
        condition.value = conditionValue;
    }

    return condition;
};

export interface BuilderFilterDiscard {
    reason: "incomplete" | "invalid";
    field: string;
}

export interface BuilderFilterDiscardReport {
    reason: "condition_discarded";
    discarded: BuilderFilterDiscard[];
}

const discardFieldForNode = (node: BuilderQueryNode) => {
    if (node.type === "condition") {
        return node.field?.trim() || "condition";
    }

    if (node.type === "exists") {
        return node.table?.trim() ? "EXISTS " + node.table.trim() : "EXISTS";
    }

    return "logical group";
};

const pushDiscard = (
    discarded: BuilderFilterDiscard[],
    node: BuilderQueryNode,
    reason: BuilderFilterDiscard["reason"]
) => {
    discarded.push({ reason, field: discardFieldForNode(node) });
};

const isBlankBuilderNode = (node: BuilderQueryNode): boolean => {
    if (node.type === "condition") {
        return (
            !node.field?.trim() &&
            !node.operator?.trim() &&
            (node.value === undefined || node.value === null || node.value === "")
        );
    }

    if (node.type === "logical") {
        return node.conditions.length > 0 && node.conditions.every(isBlankBuilderNode);
    }

    return false;
};

const sanitizeBuilderQueryNode = (
    node: BuilderQueryNode,
    columns: SysColumn[] = [],
    discarded: BuilderFilterDiscard[] = []
): Node | null => {
    if (isBlankBuilderNode(node)) {
        return null;
    }

    if (node.type === "condition") {
        const field = node.field?.trim();
        const operator = node.operator?.trim();

        if (!field || !operator) {
            pushDiscard(discarded, node, "incomplete");
            return null;
        }

        if (
            !isUnaryOperator(operator) &&
            (node.value === undefined || node.value === null || node.value === "")
        ) {
            pushDiscard(discarded, node, "incomplete");
            return null;
        }

        const column = columnForField(field, columns);
        const value = sanitizeTemporalValue(column?.column_type, operator, node.value);

        if (!isUnaryOperator(operator) && value === null) {
            pushDiscard(discarded, node, "invalid");
            return null;
        }

        return {
            type: "condition",
            field,
            operator,
            ...(isUnaryOperator(operator) ? {} : { value }),
        };
    }

    if (node.type === "exists") {
        const table = node.table?.trim();
        if (!table) {
            pushDiscard(discarded, node, "invalid");
            return null;
        }

        const discardedBefore = discarded.length;
        const conditions = node.conditions
            .map((condition) => sanitizeBuilderQueryNode(condition, columns, discarded))
            .filter((condition): condition is ConditionNode => condition?.type === "condition");

        if (conditions.length === 0) {
            if (discarded.length === discardedBefore) pushDiscard(discarded, node, "invalid");
            return null;
        }

        return {
            type: "exists",
            table,
            conditions,
        };
    }

    const discardedBefore = discarded.length;
    const conditions = node.conditions
        .map((condition) => sanitizeBuilderQueryNode(condition, columns, discarded))
        .filter(Boolean) as Node[];

    if (conditions.length === 0) {
        if (discarded.length === discardedBefore) pushDiscard(discarded, node, "invalid");
        return null;
    }

    return {
        type: "logical",
        operator: node.operator === "or" ? "or" : "and",
        conditions,
    };
};

const createBuilderConditionNode = (node: ConditionNode): BuilderConditionNode => ({
    id: crypto.randomUUID(),
    type: "condition",
    field: node.field,
    operator: node.operator,
    value: node.value,
});

const createBuilderExistsNode = (node: ExistsNode): BuilderQueryNode => ({
    id: crypto.randomUUID(),
    type: "exists",
    table: node.table,
    conditions: node.conditions.map(createBuilderConditionNode),
});

const createBuilderQueryNode = (node: Node): BuilderQueryNode => {
    if (node.type === "condition") {
        return createBuilderConditionNode(node);
    }

    if (node.type === "exists") {
        return createBuilderExistsNode(node);
    }

    return {
        id: crypto.randomUUID(),
        type: "logical",
        operator: node.operator,
        conditions: node.conditions.map(createBuilderQueryNode),
    };
};

export const getRecordListFilterParamKey = () => FILTER_PARAM_KEY;

export const getRecordListSortParamKey = () => SORT_PARAM_KEY;

export const serializeFilterToParam = (filter: LogicalNode | null) => {
    if (!filter) {
        return null;
    }

    return encodeBase64Url(JSON.stringify(compactQueryNode(filter)));
};

export const deserializeFilterFromParam = (value: string | null): LogicalNode | null => {
    if (!value) {
        return null;
    }

    try {
        const parsed = JSON.parse(decodeBase64Url(value)) as CompactQueryNode;
        const expanded = expandQueryNode(parsed);

        return expanded.type === "logical"
            ? expanded
            : {
                type: "logical",
                operator: "and",
                conditions: [expanded],
            };
    } catch (error) {
        console.warn("[RecordListUrlState] Failed to decode filter from URL", error);
        return null;
    }
};

export const serializeSortToParam = (sort: SortPreferences | null) => {
    const normalizedSort = normalizeSortPreferences(sort);

    if (!normalizedSort) {
        return NULL_SORT_SENTINEL;
    }

    return normalizedSort
        .map(({ field, sort }) => `${encodeURIComponent(field)}:${sort === "desc" ? "d" : "a"}`)
        .join(",");
};

export const deserializeSortFromParam = (
    value: string | null
): { hasExplicitSort: boolean; sortPreference: SortPreferences | null } => {
    if (!value) {
        return { hasExplicitSort: false, sortPreference: null };
    }

    if (value === NULL_SORT_SENTINEL) {
        return { hasExplicitSort: true, sortPreference: null };
    }

    const sortPreference = normalizeSortPreferences(
        value.split(",").map((entry) => {
            const separatorIndex = entry.lastIndexOf(":");

            if (separatorIndex <= 0) {
                return null;
            }

            const field = decodeURIComponent(entry.slice(0, separatorIndex));
            const direction = entry.slice(separatorIndex + 1);

            if (!field || (direction !== "a" && direction !== "d")) {
                return null;
            }

            return {
                field,
                sort: direction === "d" ? "desc" : "asc",
            };
        })
    );

    return sortPreference
        ? { hasExplicitSort: true, sortPreference }
        : { hasExplicitSort: false, sortPreference: null };
};

export const combineRecordListFilters = (
    baseFilter?: LogicalNode | null,
    appliedFilter?: LogicalNode | null
): LogicalNode | null => {
    if (baseFilter && appliedFilter) {
        return {
            type: "logical",
            operator: "and",
            conditions: [baseFilter, appliedFilter],
        };
    }

    return appliedFilter ?? baseFilter ?? null;
};

export const sanitizeBuilderFilter = (
    filter: BuilderLogicalNode | null,
    columns: SysColumn[] = []
): LogicalNode | null => {
    if (!filter) {
        return null;
    }

    const sanitized = sanitizeBuilderQueryNode(filter, columns);

    if (!sanitized) {
        return null;
    }

    return sanitized.type === "logical"
        ? sanitized
        : {
            type: "logical",
            operator: "and",
            conditions: [sanitized],
        };
};

export const sanitizeBuilderFilterWithReport = (
    filter: BuilderLogicalNode | null,
    columns: SysColumn[] = []
): { filter: LogicalNode | null; partialApplication: BuilderFilterDiscardReport | null } => {
    if (!filter) {
        return { filter: null, partialApplication: null };
    }

    const discarded: BuilderFilterDiscard[] = [];
    const sanitized = sanitizeBuilderQueryNode(filter, columns, discarded);
    const result = sanitized
        ? sanitized.type === "logical"
            ? sanitized
            : {
                type: "logical" as const,
                operator: "and" as const,
                conditions: [sanitized],
            }
        : null;
    const uniqueDiscarded = discarded.filter((item, index, items) =>
        items.findIndex((candidate) => candidate.reason === item.reason && candidate.field === item.field) === index
    );

    return {
        filter: result,
        partialApplication: uniqueDiscarded.length > 0
            ? { reason: "condition_discarded", discarded: uniqueDiscarded }
            : null,
    };
};

export const createBuilderFilterFromApplied = (
    filter: LogicalNode | null
): BuilderLogicalNode => {
    if (!filter) {
        return {
            id: crypto.randomUUID(),
            type: "logical",
            operator: "and",
            conditions: [
                {
                    id: crypto.randomUUID(),
                    type: "condition",
                    field: "",
                    operator: "",
                    value: undefined,
                },
            ],
        };
    }

    const builderNode = createBuilderQueryNode(filter);

    return builderNode.type === "logical"
        ? builderNode
        : {
            id: crypto.randomUUID(),
            type: "logical",
            operator: "and",
            conditions: [builderNode],
        };
};
